import React, { useCallback, useEffect, useRef, useState } from "react";
import { playResult, playFanfare, playClap } from "@/lib/feedbackSounds";
import { SoundToggle } from "@/components/SoundToggle";
import { Play, Hand, RotateCcw, Ear, Trophy, Mic } from "lucide-react";
import { openMic } from "@/lib/mic";
import { StaffViewer } from "@/components/StaffViewer";
import { playTick, audioClock, outputLatencySec } from "@/lib/audio";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RHYTHM_EVENT } from "@/components/RhythmLeaderboard";
import { WeeklyBoard } from "@/components/WeeklyBoard";
import { MicSoundCheck } from "@/components/MicSoundCheck";
import { ClapTimeline } from "@/components/ClapTimeline";

const T3 = 1 / 3;
const near = (a, b) => Math.abs(a - b) < 1e-3;
const durOf = (d) => (near(d, T3) ? { duration: "eighth", tuplet: 3 } : near(d, 0.25) ? { duration: "16th" } : near(d, 1.5) ? { duration: "quarter", dot: true }
  : near(d, 3) ? { duration: "half", dot: true } : { duration: { 0.5: "eighth", 1: "quarter", 2: "half", 4: "whole" }[d] });
const LEVELS = [
  { id: 0, label: "Semibreves", pool: [[4]] },
  { id: 1, label: "Crotchets", pool: [[1, 1, 1, 1]] },
  { id: 2, label: "+ Minims", pool: [[1, 1, 2], [2, 1, 1], [2, 2], [1, 2, 1], [1, 1, 1, 1]] },
  { id: 3, label: "+ Semibreves", pool: [[4], [2, 2], [1, 1, 2], [2, 1, 1], [1, 1, 1, 1]] },
  { id: 4, label: "+ Quavers & rests", pool: [[0.5, 0.5, 1, 2], [1, 0.5, 0.5, 1, 1], [0.5, 0.5, 0.5, 0.5, 2], [1, 1, 0.5, 0.5, 1], [2, 0.5, 0.5, 1], [1, -1, 0.5, 0.5, 1], [0.5, 0.5, 1, -1, 1]] },
  { id: 5, label: "Rests: stay silent!", pool: [[1, -1, 1, 1], [2, -1, 1], [1, 1, -2], [-1, 1, 2], [1, -1, 1, -1], [2, -2], [-1, 1, -1, 1], [1, 1, -1, 1]] },
  { id: 6, label: "Semiquavers", pool: [[0.25, 0.25, 0.25, 0.25, 1, 2], [1, 0.25, 0.25, 0.25, 0.25, 2], [0.5, 0.5, 0.25, 0.25, 0.25, 0.25, 2], [0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 0.25, 2], [1, 1, 0.25, 0.25, 0.25, 0.25, 1]] },
  { id: 7, label: "Dotted crotchets", pool: [[1.5, 0.5, 2], [1.5, 0.5, 1.5, 0.5], [1, 1.5, 0.5, 1], [2, 1.5, 0.5], [1.5, 0.5, 1, 1]] },
  { id: 8, label: "Triplets", pool: [[T3, T3, T3, 1, 2], [1, T3, T3, T3, 2], [T3, T3, T3, T3, T3, T3, 2], [1, 1, T3, T3, T3, 1], [T3, T3, T3, 1, T3, T3, T3, 1]] },
];
const LEVELS68 = [{ id: "6-8", label: "6/8: quavers & dotted crotchets", pool: [[0.5, 0.5, 0.5, 0.5, 0.5, 0.5], [1.5, 1.5], [1, 0.5, 1, 0.5], [1.5, 0.5, 0.5, 0.5], [3], [0.5, 0.5, 0.5, 1.5], [1, 0.5, 1.5]] }];
const COUNTS68 = { six: ["1", "2", "3", "4", "5", "6"], two: ["1", "&", "a", "2", "&", "a"] };
const REST = { 0.5: "quaver", 1: "crotchet", 2: "minim", 4: "semibreve" };
const BPMS = [60, 80, 100];
const pick = (a) => a[Math.floor(Math.random() * a.length)];
const makePattern = (lvl, set = LEVELS) => [pick(set[lvl].pool), pick(set[lvl].pool)];
export const isoWeek = (d = new Date()) => {
  const t = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const y = t.getUTCFullYear();
  const wk = Math.ceil(((t - Date.UTC(y, 0, 1)) / 86400000 + 1) / 7);
  return `${y}-W${String(wk).padStart(2, "0")}`;
};
const seeded = (str) => { let h = 2166136261; for (const ch of str) h = Math.imul(h ^ ch.charCodeAt(0), 16777619); return () => { h = Math.imul(h ^ (h >>> 15), 2246822507); h = Math.imul(h ^ (h >>> 13), 3266489909); return ((h ^= h >>> 16) >>> 0) / 4294967296; }; };
const weeklyPattern = () => { const r = seeded(`jt-${isoWeek()}`); const pool = LEVELS.find((L) => L.id === 3).pool; return [pool[Math.floor(r() * pool.length)], pool[Math.floor(r() * pool.length)]]; };
const onsetsOf = (bars) => { const out = []; let t = 0; bars.flat().forEach((d) => { if (d > 0) out.push(t); t += Math.abs(d); }); return out; };
const grade = (ms) => (ms <= 70 ? 100 : ms <= 140 ? 70 : ms <= 220 ? 40 : 0);
const COLOR = { 100: "#10B981", 70: "#10B981", 40: "#F59E0B", 0: "#EF4444" };

const NAME = { 0.25: "semiquaver", 0.5: "quaver", 1: "crotchet", 1.5: "dotted crotchet", 2: "minim", 3: "dotted minim", 4: "semibreve" };
const nameOf = (len) => (near(len, T3) ? "triplet" : NAME[len]);
const fmtLen = (len) => (near(len, T3) ? "⅓" : { 0.25: "¼", 0.5: "½", 1.5: "1½" }[len] || len);
// What's happening on the current beat: "clap", "hold" or "rest".
const actionAt = (bars, beat) => {
  if (beat == null) return null;
  let t = 0;
  for (const d of bars.flat()) { const len = Math.abs(d); if (beat >= t - 1e-3 && beat < t + len - 1e-3) return d < 0 ? "rest" : near(beat, t) ? "clap" : "hold"; t += len; }
  return null;
};

// One strip per note, as wide as its value: CLAP once, then HOLD for the remaining beats.
const ClapGuide = ({ bars, beat, testId, six8 }) => {
  const q = (len) => len * 2;
  let t = 0;
  return (
    <div className="mt-3" data-testid={`${testId}-guide`}>
      <p className="mb-1.5 text-xs font-semibold text-muted-foreground">Clap once on every note, then <span className="text-primary">hold</span> your hands together for the rest of a long note. Don't clap on held beats!{bars.flat().some((d) => Math.abs(d) < 0.5 - 1e-3) && " A dot (●) means one quick clap."}{six8 && <span className="sm:hidden"> A dot (●) means clap once, a dash (–) means rest, and ♪ shows how many quavers.</span>}</p>
      <div className="overflow-x-auto pb-1">
      <div className="flex gap-1" style={{ minWidth: `${bars.flat().length * (six8 ? 26 : 38)}px` }}>
        {bars.map((bar, b) => (
          <div key={b} className={`flex min-w-0 flex-1 gap-1 ${b ? "border-l-2 border-foreground/40 pl-1" : ""}`}>
            {bar.map((d, k) => {
              const len = Math.abs(d), s = t; t += len;
              const on = beat != null && beat >= s - 1e-3 && beat < s + len - 1e-3;
              const held = on && !near(beat, s);
              const tiny = len < 0.5 - 1e-3;
              return (
                <div key={k} style={{ flexGrow: len }} data-testid={`${testId}-guide-${b}-${k}`}
                  className={`flex min-w-0 basis-0 flex-col justify-center overflow-hidden rounded-lg border ${tiny ? "px-0" : six8 ? "px-0 sm:px-1" : "px-1 sm:px-1.5"} py-1 text-center transition-colors ${on ? "border-primary bg-primary/20" : "border-border bg-background/60"}`}>
                  {tiny ? (
                    <>
                      <span className={`text-[11px] font-bold leading-none ${on ? "text-primary" : "text-foreground"}`} aria-label="clap">●</span>
                      <span className="text-[10px] text-muted-foreground">{fmtLen(len)}</span>
                    </>
                  ) : six8 ? (
                    <>
                      <span className={`text-[9px] font-bold uppercase leading-tight sm:text-[11px] ${d < 0 ? "text-muted-foreground" : on && !held ? "text-primary" : "text-foreground"}`}>{d < 0 ? (len > 0.5 ? "rest" : <><span className="sm:hidden" aria-label="rest">–</span><span className="hidden sm:inline">rest</span></>) : len > 0.5 ? "clap" : <><span className="sm:hidden" aria-label="clap">●</span><span className="hidden sm:inline">clap</span></>}</span>
                      {len > 0.5 && d > 0 && <span className={`text-[9px] font-bold uppercase leading-tight sm:text-[11px] ${held ? "text-primary" : "text-muted-foreground"}`} data-testid={`${testId}-guide-hold-${b}-${k}`}>+ hold {q(len) - 1}</span>}
                      <span className="text-[10px] leading-tight text-muted-foreground">{d < 0 ? <><span className="md:hidden">{q(len)}</span><span className="hidden md:inline">silent</span></> : <><span className="md:hidden">{q(len)} ♪</span><span className="hidden md:inline">{len > 0.5 && <span className="hidden xl:inline">{nameOf(len)} · </span>}{q(len)} quaver{q(len) > 1 ? "s" : ""}</span></>}</span>
                    </>
                  ) : (
                    <>
                      <span className={`text-[9px] font-bold uppercase tracking-tight sm:text-[11px] ${d < 0 ? "text-muted-foreground" : on && !held ? "text-primary" : "text-foreground"}`}>{d < 0 ? "rest" : "clap"}{(six8 ? len > 0.5 : len > 1) && d > 0 && <span className={held ? "text-primary" : "text-muted-foreground"}> + hold {six8 ? q(len) - 1 : fmtLen(len - 1)}</span>}</span>
                      <span className="text-[10px] text-muted-foreground">{d < 0 ? "silent" : <><span className="hidden sm:inline">{nameOf(len)} · </span>{six8 ? `${q(len)} quaver${q(len) > 1 ? "s" : ""}` : `${fmtLen(len)} beat${len > 1 ? "s" : ""}`}</>}</span>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      </div>
    </div>
  );
};

const buildNotes = (bars, results) => {
  const notes = []; let k = 0;
  bars.forEach((bar, b) => {
    bar.forEach((d) => {
      if (d < 0) { notes.push({ rest: REST[-d] }); return; }
      const r = results?.[k]; notes.push({ pitch: "C5", ...durOf(d), color: r == null ? undefined : COLOR[r] }); k += 1;
    });
    notes.push({ barline: b === bars.length - 1 ? "double" : true });
  });
  return notes;
};

export const RhythmClap = ({ testId = "rhythm-clap", levels, startLevel, meter = "4/4" }) => {
  const { user } = useAuth();
  const six8 = meter === "6/8";
  const SET = six8 ? LEVELS68 : LEVELS;
  const canChallenge = !levels && !six8;
  const [countMode, setCountMode] = useState("six");
  const [challenge, setChallenge] = useState(false);
  const first = six8 ? 0 : Math.max(0, LEVELS.findIndex((L) => L.id === (startLevel ?? levels?.[0] ?? 1)));
  const [lvl, setLvl] = useState(first);
  const [bpm, setBpm] = useState(six8 ? 60 : 80);
  const [bars, setBars] = useState(() => makePattern(first, SET));
  const barLen = six8 ? 3 : 4, total = barLen * 2, step = six8 ? 0.5 : 1;
  const cin = six8 ? COUNTS68[countMode] : ["1", "2", "3", "4"];
  const [board68, setBoard68] = useState(null);
  useEffect(() => { if (six8) api.get("/clap68/leaderboard").then(({ data }) => setBoard68(data)).catch(() => {}); }, [six8]);
  const [phase, setPhase] = useState("idle");
  const [count, setCount] = useState(null);
  const [results, setResults] = useState(null);
  const [score, setScore] = useState(null);
  const [bests, setBests] = useState(() => JSON.parse(localStorage.getItem("jt_rhythm_bests") || "{}"));
  const bestKey = challenge ? `challenge-${isoWeek()}` : six8 ? `6-8-${countMode}` : LEVELS[lvl].id;
  const best = bests[bestKey] || 0;
  const timers = useRef([]);
  const taps = useRef([]);
  const t0 = useRef(0);
  const [micOn, setMicOn] = useState(false);
  const [sens, setSens] = useState(5);
  const [micErr, setMicErr] = useState("");
  const [flash, setFlash] = useState(0);
  const mic = useRef(null);
  const micRaf = useRef(0);
  const clicks = useRef([]);
  const lastOn = useRef(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const sensRef = useRef(sens);
  sensRef.current = sens;

  const micToken = useRef(0);
  const noise = useRef(0);
  const noiseSamples = useRef([]);
  const [level, setLevel] = useState(0);
  const [heard, setHeard] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [checking, setChecking] = useState(false);
  const onChecked = useCallback((s) => { setSens(s); setTimeout(() => setChecking(false), 1500); }, []);
  const closeCheck = useCallback(() => setChecking(false), []);
  const [beat, setBeat] = useState(null);
  const stopMic = () => { micToken.current += 1; cancelAnimationFrame(micRaf.current); mic.current?.stop(); mic.current = null; };
  const startMic = async () => {
    setMicErr("");
    const tok = ++micToken.current;
    let m;
    try { m = await openMic(); } catch { setMicErr("Microphone blocked — allow it in your browser, or just tap instead."); return; }
    if (tok !== micToken.current) { m.stop(); return; }
    mic.current = m;
    const loop = () => {
      if (!mic.current) return;
      const b = mic.current.read();
      let peak = 0, idx = 0;
      for (let i = 0; i < b.length; i += 1) { const v = Math.abs(b[i]); if (v > peak) { peak = v; idx = i; } }
      const t = performance.now() - ((b.length - idx) / mic.current.ctx.sampleRate) * 1000;
      // Threshold follows the sensitivity slider but never drops below 3x the room's background level
      // (measured between the count-in clicks).
      if (phaseRef.current === "countin") noiseSamples.current.push(peak);
      else if (noiseSamples.current.length) {
        const s = noiseSamples.current.sort((a, z) => a - z);
        noise.current = s[Math.floor(s.length * 0.3)]; noiseSamples.current = [];
      }
      const thr = Math.max(0.42 - sensRef.current * 0.036, Math.min(0.7, noise.current * 3));
      const lv = Math.min(1, peak / (thr * 2));
      setLevel((old) => (Math.abs(old - lv) > 0.04 ? lv : old));
      if (peak > thr && t - lastOn.current > 140 && phaseRef.current === "play") {
        lastOn.current = t; taps.current.push(t - t0.current); setFlash((f) => f + 1);
      }
      micRaf.current = requestAnimationFrame(loop);
    };
    loop();
  };

  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => () => { clear(); stopMic(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const at = (ms, fn) => timers.current.push(setTimeout(fn, ms));
  const beatMs = 60000 / bpm;

  const listen = () => {
    clear(); setPhase("listen"); setResults(null); setScore(null);
    onsetsOf(bars).forEach((o) => at(o * beatMs, () => playClap()));
    at(total * beatMs, () => setPhase("idle"));
  };

  const finish = () => {
    taps.current = taps.current.slice().sort((a, b) => a - b).filter((t, i, a) => i === 0 || t - a[i - 1] > 120);
    const expected = onsetsOf(bars).map((o) => o * beatMs);
    if (micOn) {
      // The microphone hears each clap a little late (device delay); remove that steady lag before marking.
      const offs = expected.map((e) => taps.current.reduce((best, t) => (Math.abs(t - e) < Math.abs(best) ? t - e : best), Infinity))
        .filter((d) => Math.abs(d) <= beatMs * 0.45).sort((a, b) => a - b);
      const lag = offs.length >= Math.max(2, expected.length / 3) ? offs[Math.floor(offs.length / 2)] : 0;
      if (lag > 0 && lag <= 300) taps.current = taps.current.map((t) => t - lag);
    }
    const used = new Set();
    const offsets = [];
    const res = expected.map((e) => {
      let bi = -1, bd = Infinity;
      taps.current.forEach((t, i) => { const d = Math.abs(t - e); if (!used.has(i) && d < bd) { bd = d; bi = i; } });
      if (bi >= 0 && bd <= beatMs * 0.4) { used.add(bi); offsets.push(Math.round(taps.current[bi] - e)); return grade(bd); }
      offsets.push(null);
      return 0;
    });
    setTimeline({ expected, offsets, extras: taps.current.filter((_, i) => !used.has(i)), micAdjusted: micOn });
    const extras = taps.current.length - used.size;
    const penalty = micOn ? Math.min(20, extras * 2) : extras * 5;
    const sc = Math.max(0, Math.round(res.reduce((a, b) => a + b, 0) / res.length - penalty));
    setHeard(micOn ? { heard: taps.current.length, expected: expected.length, missed: res.filter((r) => r === 0).length, extras, noisy: noise.current * 3 > 0.42 - sensRef.current * 0.036 } : null);
    stopMic();
    setResults(res); setScore(sc); setPhase("done");
    if (sc >= 100) playFanfare(0.1, six8 ? "6/8 Clap game" : "Rhythm Clap"); else playResult(sc >= 70);
    if (sc > best) { const nb = { ...bests, [bestKey]: sc }; setBests(nb); localStorage.setItem("jt_rhythm_bests", JSON.stringify(nb)); }
    if (user && six8) api.post("/clap68/score", { score: sc }).then(({ data }) => setBoard68(data)).catch(() => {});
    if (user && challenge) api.post("/rhythm-clap/score", { score: sc, level: LEVELS[lvl].id, bpm })
      .then(({ data }) => window.dispatchEvent(new CustomEvent(RHYTHM_EVENT, { detail: data }))).catch(() => {});
  };

  const start = () => {
    clear(); stopMic(); taps.current = []; clicks.current = []; lastOn.current = 0; noise.current = 0; noiseSamples.current = []; setHeard(null); setTimeline(null); setResults(null); setScore(null); setPhase("countin");
    if (micOn) startMic();
    const ac = audioClock();
    const lead = 0.12, T = ac.currentTime + lead, beatS = (beatMs * step) / 1000;
    const begin = lead * 1000 + cin.length * step * beatMs;
    // Beat 1 of the rhythm as heard through the speakers, on the performance.now() clock.
    t0.current = performance.now() + begin + outputLatencySec() * 1000;
    const strong = (b) => (six8 ? (b * step) % 1.5 === 0 : b % 4 === 0);
    for (let b = 0; b < cin.length; b += 1) {
      playTick(b === 0 ? 1320 : strong(b) ? 1100 : 990, 0.06, 1, T + b * beatS);
      at(lead * 1000 + b * step * beatMs, () => { setCount(b + 1); clicks.current.push(performance.now()); });
    }
    at(begin, () => { setCount(null); setPhase("play"); });
    const n = total / step;
    for (let b = 0; b < n; b += 1) {
      if (!micOn) playTick(strong(b) ? 880 : 660, 0.04, strong(b) ? 0.8 : 0.5, T + (cin.length + b) * beatS);
      at(begin + b * step * beatMs, () => setBeat(b * step));
    }
    at(begin + total * beatMs, () => setBeat(null));
    at(begin + total * beatMs + beatMs * 0.4, finish);
  };

  const tap = () => {
    if (phase !== "play" && phase !== "countin") return;
    const t = performance.now() - t0.current;
    playTick(520, 0.07);
    if (t > -beatMs * 0.4) taps.current.push(t);
  };

  useEffect(() => {
    const onKey = (e) => { if (e.code === "Space" && (phase === "play" || phase === "countin")) { e.preventDefault(); tap(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const toggleChallenge = () => {
    clear(); stopMic(); setPhase("idle"); setResults(null); setScore(null);
    if (challenge) { setChallenge(false); setBars(makePattern(lvl, SET)); return; }
    setChallenge(true); setLvl(LEVELS.findIndex((L) => L.id === 3)); setBpm(80); setBars(weeklyPattern());
  };
  const newRhythm = (l = lvl) => { clear(); stopMic(); setPhase("idle"); setResults(null); setScore(null); setBars(makePattern(l, SET)); };
  const busy = phase === "countin" || phase === "play" || phase === "listen";
  const chip = (on) => `rounded-full px-3 py-1 text-xs font-medium transition-colors ${on ? "bg-primary text-primary-foreground" : "border border-border text-muted-foreground hover:border-primary hover:text-primary"}`;

  return (
    <div className="mt-5 rounded-2xl border border-primary/30 bg-card p-4 sm:p-6" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-primary"><Hand size={14} /> {six8 ? "6/8 Clap game" : "Rhythm Clap"}</p>
        <SoundToggle testId={`${testId}-sound-toggle`} />
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground" data-testid={`${testId}-best`}><Trophy size={13} className="text-primary" /> {challenge ? "Challenge" : six8 ? (countMode === "six" ? "1 to 6" : "1 & a") : `Level ${LEVELS[lvl].id}`} best: {best}%</p>
      </div>
      {canChallenge && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button onClick={toggleChallenge} disabled={busy} data-testid={`${testId}-challenge`}
            className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${challenge ? "bg-primary text-primary-foreground" : "border border-primary text-primary hover:bg-primary/10"}`}>
            <Trophy size={12} className="mr-1 inline" /> {challenge ? `Weekly challenge on (${isoWeek()})` : "Take this week's challenge"}
          </button>
          <span className="text-[11px] text-muted-foreground" data-testid={`${testId}-mode`}>{challenge ? (user ? "Your best score goes on this week's leaderboard." : "Log in to put your score on the leaderboard.") : "Free practice — not ranked."}</span>
        </div>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {six8 && Object.keys(COUNTS68).map((m) => (
          <button key={m} disabled={busy} onClick={() => setCountMode(m)} className={chip(countMode === m)} data-testid={`${testId}-count-${m}`}>{m === "six" ? "Count 1 2 3 4 5 6" : "Count 1 & a 2 & a"}</button>
        ))}
        {!six8 && LEVELS.map((L, i) => ((levels ? !levels.includes(L.id) : L.id > 5) ? null : <button key={L.id} disabled={busy || challenge} onClick={() => { setLvl(i); newRhythm(i); }} className={chip(lvl === i)} data-testid={`${testId}-level-${L.id}`}>Level {L.id}: {L.label}</button>))}
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {BPMS.map((b) => <button key={b} disabled={busy || challenge} onClick={() => setBpm(b)} className={chip(bpm === b)} data-testid={`${testId}-bpm-${b}`}>{b} bpm</button>)}
      </div>
      <div className="mt-4 rounded-xl border border-border bg-background/60 p-3">
        <div className={`mb-2 flex items-center justify-center gap-3 sm:gap-5 ${phase === "countin" ? "" : "opacity-40"}`} data-testid={`${testId}-countin`} aria-live="polite">
          <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Count in</span>
          {cin.map((lab, j) => {
            const k = j + 1;
            return (
              <span key={k} data-testid={`${testId}-countin-${k}`}
                className={`flex items-center justify-center rounded-full font-serif font-bold transition-transform duration-150 ${six8 ? "h-9 w-9 text-xl sm:h-11 sm:w-11 sm:text-2xl" : "h-11 w-11 text-2xl sm:h-12 sm:w-12 sm:text-3xl"} ${phase === "countin" && count === k ? "scale-110 bg-primary text-primary-foreground" : phase === "countin" && count > k ? "bg-primary/25 text-primary" : "border border-border text-muted-foreground"}`}>{lab}</span>
            );
          })}
        </div>
        <StaffViewer clef="percussion" time={meter} notes={buildNotes(bars, results)} width={560} playOnClick={false} testId={`${testId}-staff`} />
        <ClapGuide bars={bars} beat={phase === "play" ? beat : null} testId={testId} six8={six8} />
        {six8 && (
          <div className="mt-2 flex gap-1 font-mono text-xs font-bold" data-testid={`${testId}-counts`}>
            {[0, 1].map((b) => (
              <div key={b} className={`flex flex-1 justify-around ${b ? "border-l-2 border-foreground/40" : ""}`}>
                {cin.map((lab, j) => <span key={j} className={phase === "play" && beat != null && near(beat, b * 3 + j * 0.5) ? "text-primary" : j % 3 === 0 ? "text-foreground" : "text-muted-foreground"}>{lab}</span>)}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <button onClick={listen} disabled={busy} className={chip(false)} data-testid={`${testId}-listen`}><Ear size={13} className="mr-1 inline" /> Listen first</button>
        <button onClick={start} disabled={busy || checking} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform hover:scale-105 disabled:opacity-50" data-testid={`${testId}-start`}><Play size={14} /> Start</button>
        <button onClick={() => newRhythm()} disabled={busy || challenge} className={chip(false)} data-testid={`${testId}-new`}><RotateCcw size={13} className="mr-1 inline" /> New rhythm</button>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-4 rounded-xl border border-border px-3 py-2" data-testid={`${testId}-mic-panel`}>
        <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
          <input type="checkbox" checked={micOn} disabled={busy || checking} onChange={(e) => { setMicOn(e.target.checked); setChecking(e.target.checked); }} className="accent-[hsl(var(--primary))]" data-testid={`${testId}-mic-toggle`} />
          <Mic size={13} className="text-primary" /> Use microphone (clap for real)
        </label>
        {micOn && (
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            Sensitivity
            <input type="range" min="1" max="10" value={sens} onChange={(e) => setSens(Number(e.target.value))} className="w-28 accent-[hsl(var(--primary))]" data-testid={`${testId}-mic-sensitivity`} />
            <span className="w-4 font-mono">{sens}</span>
          </label>
        )}
        {micOn && phase === "play" && <span key={flash} className="ml-auto h-3 w-3 animate-ping rounded-full bg-primary" data-testid={`${testId}-mic-flash`} />}
        {micOn && checking && <MicSoundCheck onDone={onChecked} onCancel={closeCheck} testId={testId} />}
        {micOn && !checking && !busy && <button type="button" onClick={() => setChecking(true)} data-testid={`${testId}-sound-check-redo`} className="text-xs text-primary hover:underline">Redo sound check</button>}
        {micOn && <span className="w-full rounded-lg bg-primary/10 px-3 py-2 text-xs font-medium text-foreground" data-testid={`${testId}-mic-quiet-tip`}>Before you start, make sure the room is quiet: turn off music and the TV, and close the door. Other sounds can be heard as claps and lower your score.</span>}
        {micOn && <span className="w-full text-[11px] text-muted-foreground">With the microphone on, the clicks stop after the count-in so they aren't heard as claps — follow the flashing beat dots instead. Too many extra claps? Lower the sensitivity. Claps not being heard? Raise it, or clap closer to the microphone.</span>}
        {micOn && busy && (
          <span className="flex w-full items-center gap-2 text-[11px] text-muted-foreground" data-testid={`${testId}-mic-level`}>
            Mic level
            <span className="relative h-2 flex-1 overflow-hidden rounded-full bg-border">
              <span className="absolute inset-y-0 left-0 bg-primary transition-[width] duration-75" style={{ width: `${Math.round(level * 100)}%` }} />
              <span className="absolute inset-y-0 left-1/2 w-0.5 bg-foreground/60" title="Claps need to pass this line" />
            </span>
          </span>
        )}
        {micErr && <span className="w-full text-xs text-red-400" data-testid={`${testId}-mic-error`}>{micErr}</span>}
      </div>
      {phase === "play" && micOn && (
        <div className="mt-3 flex justify-center gap-2" data-testid={`${testId}-beat-dots`}>
          {Array.from({ length: total / step }, (_, k) => <span key={k} className={`h-3 w-3 rounded-full transition-colors ${beat != null && near(beat, k * step) ? "bg-primary" : "bg-border"} ${k === barLen / step ? "ml-3" : ""}`} />)}
        </div>
      )}
      <button onPointerDown={tap} disabled={!busy || phase === "listen"} data-testid={`${testId}-tap`}
        className={`mt-4 flex h-24 w-full select-none items-center justify-center rounded-2xl border-2 text-lg font-semibold transition-colors ${phase === "play" ? "border-primary bg-primary/15 text-primary active:bg-primary/30" : "border-border text-muted-foreground"}`}>
        {phase === "countin" ? `Get ready… ${count || ""}` : phase === "play" ? ({ clap: "CLAP!", hold: "HOLD… (don't clap)", rest: "Rest… (silent)" }[actionAt(bars, beat)] || "TAP / CLAP HERE (or press space)") : phase === "listen" ? "Listening…" : "Press Start, then tap here on every note"}
      </button>
      {score != null && (
        <p className="mt-3 text-center text-sm" data-testid={`${testId}-score`}>
          <strong className={`rounded-full px-3 py-0.5 font-serif text-2xl ${score >= 70 ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-500"}`}>{score}%</strong>{" "}
          <span className="text-muted-foreground">{score >= 90 ? "Brilliant timing!" : score >= 70 ? (challenge || six8 ? "Great — can you beat it this week?" : "Great — try the next level.") : score >= 40 ? "Nearly — count out loud and try again." : "Keep going — press Listen first, then try again."}</span>
          <span className="mt-1 block text-xs text-muted-foreground">Green = on time · Amber = close · Red = missed</span>
          {heard && (
            <span className="mt-1 block text-xs text-muted-foreground" data-testid={`${testId}-mic-heard`}>
              The microphone heard {heard.heard} clap{heard.heard === 1 ? "" : "s"} ({heard.expected} expected).
              {heard.extras > 2 ? " It heard extra sounds: make sure the room is quiet, or lower the sensitivity." : heard.missed > 1 && heard.noisy ? " The room sounds noisy, so some claps were lost in the background. Find a quieter spot and try again." : heard.missed > 1 ? " Some claps were too quiet to hear: clap closer to the microphone or raise the sensitivity." : ""}
            </span>
          )}
        </p>
      )}
      {score != null && timeline && <ClapTimeline {...timeline} totalMs={total * beatMs} beatMs={beatMs} testId={testId} />}
      {six8 && <WeeklyBoard title="This week's 6/8 Clap board" board={board68} testId={testId} user={user} field="score" unit={(v) => `${v}%`} empty="Finish a clap to get on the board." />}
    </div>
  );
};

export default RhythmClap;
